// DPR Blocker Master management (permission: dpr_blocker_master.manage).
// Values feed the Blockers dropdown in Generate DPR; DPR docs snapshot
// code+name by stable ID, so renames/deactivation never change history.
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Plus, Pencil, Check, OctagonAlert } from "lucide-react";

const inputCls = "w-full border border-slate-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:border-[#F97316]";

const SCOPES = ["Client", "Radiants", "Other"];

export default function DprBlockerMasterModal({ onClose }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ code: "", name: "", description: "", scope: "Other" });
  const [editId, setEditId] = useState(null);
  const [edit, setEdit] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/dpr-blocker-masters");
      setRows(r.data?.rows || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load blockers"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Blocker name is mandatory"); return; }
    setBusy(true);
    try {
      await api.post("/dpr-blocker-masters", form);
      toast.success("Blocker added");
      setForm({ code: "", name: "", description: "", scope: "Other" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add blocker"); }
    finally { setBusy(false); }
  };
  const saveEdit = async () => {
    try {
      await api.put(`/dpr-blocker-masters/${editId}`, {
        name: edit.name, description: edit.description, scope: edit.scope || "Other",
        display_order: parseInt(edit.display_order, 10) || 0 });
      toast.success("Blocker updated — existing DPRs keep their snapshot");
      setEditId(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update"); }
  };
  const toggleActive = async (b) => {
    const active = b.active !== false;
    try {
      await api.put(`/dpr-blocker-masters/${b.id}`, { active: !active });
      toast.success(active
        ? "Deactivated — hidden from new DPR blocker selections; existing DPRs keep it"
        : "Activated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-start justify-center overflow-y-auto py-10" data-testid="dpr-blocker-master-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="text-sm font-bold text-slate-900 flex items-center gap-2"><OctagonAlert size={15} className="text-[#F97316]" /> DPR Blocker Master</div>
          <button data-testid="dpr-blocker-master-close" onClick={onClose} className="text-slate-500 hover:text-slate-800"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="flex items-end gap-2 border border-slate-200 rounded p-3 bg-slate-50">
            <div className="w-24"><div className="text-[9px] uppercase font-bold text-slate-400">Code</div>
              <input data-testid="dpr-blk-add-code" className={inputCls} placeholder="auto" value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })} /></div>
            <div className="flex-1"><div className="text-[9px] uppercase font-bold text-slate-400">Name *</div>
              <input data-testid="dpr-blk-add-name" className={inputCls} value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="flex-1"><div className="text-[9px] uppercase font-bold text-slate-400">Description</div>
              <input data-testid="dpr-blk-add-desc" className={inputCls} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="w-28"><div className="text-[9px] uppercase font-bold text-slate-400">Scope</div>
              <select data-testid="dpr-blk-add-scope" className={inputCls} value={form.scope}
                onChange={(e) => setForm({ ...form, scope: e.target.value })}>
                {SCOPES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select></div>
            <button data-testid="dpr-blk-add-btn" onClick={create} disabled={busy}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-white bg-[#F97316] rounded hover:bg-orange-600 disabled:opacity-50">
              <Plus size={12} /> Add
            </button>
          </div>
          <div className="border border-slate-200 rounded overflow-x-auto">
            <table className="w-full min-w-[600px] text-xs" data-testid="dpr-blocker-master-table">
              <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Order</th>
                  <th className="px-3 py-2 text-left">Code</th>
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-left">Description</th>
                  <th className="px-3 py-2 text-left">Scope</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400" data-testid="dpr-blk-empty">No blockers yet — add the first one above</td></tr>
                )}
                {rows.map((b) => (
                  <tr key={b.id} className="border-t border-slate-100" data-testid={`dpr-blk-row-${b.code}`}>
                    {editId === b.id ? (
                      <>
                        <td className="px-2 py-1.5 w-16"><input data-testid="dpr-blk-edit-order" type="number" className={inputCls} value={edit.display_order}
                          onChange={(e) => setEdit({ ...edit, display_order: e.target.value })} /></td>
                        <td className="px-3 py-1.5 font-mono text-slate-400">{b.code}</td>
                        <td className="px-2 py-1.5"><input data-testid="dpr-blk-edit-name" className={inputCls} value={edit.name}
                          onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></td>
                        <td className="px-2 py-1.5"><input data-testid="dpr-blk-edit-desc" className={inputCls} value={edit.description}
                          onChange={(e) => setEdit({ ...edit, description: e.target.value })} /></td>
                        <td className="px-2 py-1.5 w-24"><select data-testid="dpr-blk-edit-scope" className={inputCls} value={edit.scope || "Other"}
                          onChange={(e) => setEdit({ ...edit, scope: e.target.value })}>
                          {SCOPES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select></td>
                        <td className="px-3 py-1.5 text-slate-400">—</td>
                        <td className="px-2 py-1.5 text-right whitespace-nowrap">
                          <button data-testid="dpr-blk-edit-save" onClick={saveEdit} className="text-emerald-700 hover:text-emerald-900 mr-2" title="Save"><Check size={14} /></button>
                          <button data-testid="dpr-blk-edit-cancel" onClick={() => setEditId(null)} className="text-slate-400 hover:text-slate-700" title="Cancel"><X size={14} /></button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-3 py-1.5 tabular-nums text-slate-500">{b.display_order}</td>
                        <td className="px-3 py-1.5 font-mono font-bold">{b.code}</td>
                        <td className="px-3 py-1.5">{b.name}</td>
                        <td className="px-3 py-1.5 text-slate-500">{b.description || "—"}</td>
                        <td className="px-3 py-1.5" data-testid={`dpr-blk-scope-${b.code}`}>
                          <span className={`inline-block px-1.5 py-0.5 text-[9px] uppercase font-bold border rounded ${
                            b.scope === "Client" ? "bg-sky-50 text-sky-700 border-sky-300"
                            : b.scope === "Radiants" ? "bg-orange-50 text-orange-700 border-orange-300"
                            : b.scope ? "bg-slate-100 text-slate-600 border-slate-300" : "bg-white text-slate-300 border-slate-200"}`}>
                            {b.scope || "—"}
                          </span>
                        </td>
                        <td className="px-3 py-1.5">
                          <span className={`inline-block px-1.5 py-0.5 text-[9px] uppercase font-bold border rounded ${b.active !== false ? "bg-emerald-50 text-emerald-700 border-emerald-300" : "bg-slate-100 text-slate-500 border-slate-300"}`}>
                            {b.active !== false ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 text-right whitespace-nowrap">
                          <button data-testid={`dpr-blk-edit-${b.code}`} title="Edit"
                            onClick={() => { setEditId(b.id); setEdit({ name: b.name, description: b.description || "", scope: b.scope || "Other", display_order: b.display_order }); }}
                            className="text-slate-500 hover:text-slate-800 mr-2"><Pencil size={13} /></button>
                          <button data-testid={`dpr-blk-toggle-${b.code}`} onClick={() => toggleActive(b)}
                            className={`text-[10px] font-bold px-2 py-0.5 border rounded ${b.active !== false ? "text-rose-700 border-rose-300 hover:bg-rose-50" : "text-emerald-700 border-emerald-300 hover:bg-emerald-50"}`}>
                            {b.active !== false ? "Deactivate" : "Activate"}
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-[10px] text-slate-500">
            Blockers are bound to DPRs by stable ID with a code/name snapshot — renaming or deactivating a value never changes submitted DPRs. Every change is kept in an append-only history.
          </div>
        </div>
      </div>
    </div>
  );
}
